import type { HTMLAttributes } from 'react';
import { cn } from '../cn.js';
import { statusTone, type BadgeTone } from './Badge.js';

/**
 * A claim or request status, as a pill with a leading dot.
 *
 * Distinct from `Badge` rather than a variant of it for two reasons: the dot is a second,
 * non-colour signal (see `statusTone`'s comment on why colour is never the only one), and the
 * vocabulary translation below.
 *
 * THE LABEL IS NOT THE STATUS. `settled` is the contract's terminal money state and the designs
 * call it "Paid", which is the right word for an employee reading their own claim. That mapping
 * lives here so every screen says the same thing, and so nobody is shown the word the state machine
 * happens to use internally. The mobile app has a matching component; the two are kept in step by
 * hand, as the architecture map requires (@hrms/ui is web-only).
 */
const LABELS: Record<string, string> = {
  draft: 'Draft',
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  settled: 'Paid',
  withdrawn: 'Withdrawn',
  // ADDED 2026-09-17 for ExpensePaymentBatch.status — a distinct vocabulary from the claim
  // statuses above ('draft' happens to overlap, harmlessly, with the claim status of the same
  // name), so labels are added here rather than a second copy of this component.
  file_generated: 'File generated',
  paid: 'Paid',
  // Billing — Subscription.status, plus a client-only sentinel (no row exists yet) the platform
  // "who has paid" view uses for an org that never started a checkout.
  active: 'Active',
  past_due: 'Past Due',
  pending_verification: 'Pending Verification',
  no_subscription: 'No Subscription',
  // ADDED B-P2 — trialing and suspended joined SUBSCRIPTION_STATUSES this phase; cancelled existed
  // before but never had a label here either, falling back to the raw string.
  trialing: 'Trial',
  suspended: 'Suspended',
  cancelled: 'Cancelled',
  // TG-5 — ExpenseAdvance's own two extra states past the request lifecycle it otherwise shares
  // with a claim's (draft/pending/approved/rejected/withdrawn, all already covered above).
  disbursed: 'Disbursed',
  written_off: 'Written off',
  // ADDED for the agreements/e-signature module (Agreement.status) — 'draft' and 'rejected' above
  // already cover two of the six values.
  sent: 'Sent',
  viewed: 'Viewed',
  signed: 'Signed',
  expired: 'Expired',
  // ADDED for Travel Desk (ADR 0030). Trip statuses not covered above: draft / pending / approved /
  // rejected / withdrawn / cancelled / expired already exist. `pending` stays "Pending" so a
  // trip reads the same here as in every other approval list.
  invited: 'Invited',
  needs_action: 'Needs action',
  booked: 'Booked',
  settling: 'Settling',
  closed: 'Closed',
  // Desk booking lifecycle (TravelDeskBookingStatus) and refund state (TravelRefundStatus): the
  // values not already labelled above.
  options_shared: 'Options shared',
  on_hold: 'On hold',
  confirmed: 'Confirmed',
  declined: 'Declined',
  replaced: 'Replaced',
  received: 'Received',
  forfeited: 'Forfeited',
  // ADDED for the training module's display statuses (Training_Module_Plan.md §6.2) — labels only.
  // `completed`'s TONE is deliberately left at `statusTone`'s `info` fallback (see that function's
  // comment); the label here is independent of tone and safe to add regardless.
  not_started: 'Not started',
  started: 'Started',
  in_progress: 'In progress',
  completed: 'Completed',
  completed_late: 'Completed late',
  incomplete: 'Incomplete',
  due_soon: 'Due soon',
  overdue: 'Overdue',
  verified: 'Verified',
  gaps_detected: 'Gaps detected',
  not_recorded: 'Not recorded',
  waived: 'Waived',
  trial_ended: 'Trial ended',
  // ADDED for the Platform Admin notifications dashboard (NotificationDelivery.status). 'sent'
  // already exists above (shared with the agreements module).
  queued: 'Queued',
  failed: 'Failed',
  suppressed: 'Suppressed',
  skipped: 'Skipped',
};

const DOTS: Record<BadgeTone, string> = {
  neutral: 'bg-fg-muted',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
  accent: 'bg-accent',
};

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-bg-subtle text-fg-muted',
  success: 'bg-success-subtle text-success',
  warning: 'bg-warning-subtle text-warning',
  danger: 'bg-danger-subtle text-danger',
  info: 'bg-info-subtle text-info',
  accent: 'bg-accent-subtle text-accent',
};

export interface StatusPillProps extends HTMLAttributes<HTMLSpanElement> {
  status: string;
}

export function StatusPill({ status, className, ...rest }: StatusPillProps) {
  const tone = statusTone(status);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold',
        TONES[tone],
        className,
      )}
      {...rest}
    >
      <span className={cn('size-1.5 shrink-0 rounded-full', DOTS[tone])} aria-hidden="true" />
      {LABELS[status] ?? status}
    </span>
  );
}
